import React, { useMemo, useEffect } from 'react'
import moment from 'moment'
import CalendarDayEmptyState from './CalendarDayEmptyState'
import { Link, navigateTo } from 'navigation'
import { formatSubmissionSurvey } from '../../WebSurveys/utils/instrumentUtils'
import { clockIcon, instrumentIcon } from '../../../assets/assets'
import CompletedInstrumentsList from './CompletedInstrumentsList'
import ExpiredInstrumentsList from './ExpiredInstrumentsList'
import {
  getInProgressInstrumentForActivities,
  getCompletedInstanceKeys,
  removeInstrumentFromLocalStorage,
  canResumeInProgressInstrument,
  resolveSchedule,
  resolveInstrumentVisibilityConfig,
  isInstanceActiveInCurrentWindow,
  resolveActiveInstanceIdForInstrument,
} from '../utils/instrumentUtils'
import {
  isPersistentRecurringScheme,
  getInstancesCompletedForCalendarDay,
  resolveInstanceIdForCalendarDay,
} from '../utils/calendarInstrumentUtils'
import { Button } from 'components/UIElements'
import { getExpirationText } from '../utils/expirationUtils'
import { logSentryEvent, SENTRY_MESSAGES } from '../../../utils/sentry'
import InstrumentTitle from './InstrumentTitle'
import { resolveInstrumentWebSurveyPath } from '../../WebSurveys/utils/webSurveyUrl'

const parseLocalizedTitle = ({ selectedLocale, instrument }) => {
  const {
    instrument_title,
    title,
    display_name,
    displayNameTranslations,
    metadata = {},
  } = instrument || {}

  const fallbackTitle =
    instrument_title ||
    title ||
    metadata?.display_name ||
    display_name

  const translations =
    displayNameTranslations || metadata?.display_name_translations

  if (!translations) return fallbackTitle

  const formattedSelectedLocale = selectedLocale.toLowerCase()

  const localizedDisplayName = translations[formattedSelectedLocale]

  if (localizedDisplayName) return localizedDisplayName

  const locale = formattedSelectedLocale.includes('_')
    ? formattedSelectedLocale.split('_')[0]
    : formattedSelectedLocale

  return translations[locale] || fallbackTitle
}

const CalendarExpandedDay = ({
  day,
  idx,
  strings,
  selectedLocale,
  setSubmissionSurvey,
  humiIsabellaDisabled,
  instrumentsForDay,
  completedInstrumentsForDay,
  expiredInstrumentsForDay,
  noInstruments,
  noCompletedInstruments,
  noExpiredInstruments,
  studyId,
  sendEvent,
  participantId,
  allInstances = [],
  firstLogin,
}) => {
  const completedInstanceKeys = useMemo(
    () => getCompletedInstanceKeys(allInstances),
    [allInstances],
  )

  useEffect(() => {
    instrumentsForDay?.forEach((instrument) => {
      const instrumentId =
        instrument.instrument_id || instrument.identity || instrument.id
      const instanceId = resolveActiveInstanceIdForInstrument(instrument)
      const storedInstrument = getInProgressInstrumentForActivities({
        instrument,
        participantId,
        instanceId,
      })
      if (
        storedInstrument?.instance_id &&
        completedInstanceKeys.has(
          `${instrumentId}:${storedInstrument.instance_id}`,
        )
      ) {
        removeInstrumentFromLocalStorage({
          instrumentId,
          participantId,
          instanceId: storedInstrument.instance_id,
        })
      }
    })
  }, [instrumentsForDay, participantId, completedInstanceKeys])

  const getTopValue = idx => {
    const initialValue = 20 + 10 + 29 + 20 * 2

    return initialValue - 11
  }

  const getStatusText = () => {
    return 'In Progress'
  }

  return (
    <div className='calendar-day-expanded' style={{ top: getTopValue(idx) }}>
      {noInstruments && noCompletedInstruments && noExpiredInstruments ? (
        <CalendarDayEmptyState humiIsabellaDisabled={humiIsabellaDisabled} strings={strings} />
      ) : (
        <div>
          {instrumentsForDay?.map(instrument => {
            const {
              instrument_type: type,
              metadata = {},
              instrument_id,
              identity,
              id,
              depQId,
              instances,
            } = instrument

            const instrumentId =
              instrument_id || identity || id || metadata?.instrument_id

            const surveyUrl = resolveInstrumentWebSurveyPath(instrument, {
              studyId,
              instrumentType: type,
            })

            const scheduleId =
              instrument.schedule_id ||
              instrument?.instances?.[0]?.schedule_id ||
              instrument?.instances?.[0]?.scheduleId
            const schedule = resolveSchedule(metadata.schedule, scheduleId)
            const isPersistent = isPersistentRecurringScheme(schedule?.recurring)

            const activeInstances = instances?.filter((instance) => {
              if (!isPersistent && instance.completion) return false

              return isInstanceActiveInCurrentWindow(instance, schedule)
            })

            const dayMoment = moment(day)
            const calendarDayInstanceId = resolveInstanceIdForCalendarDay({
              instanceGroup:
                allInstances?.find(
                  (entry) =>
                    (entry?.instrument_id || entry?.identity || entry?.id) ===
                    instrumentId,
                ) || instrument,
              instances,
              targetDayMoment: dayMoment,
              isPersistent,
              schedule,
              firstLogin,
            })

            if (!isPersistent && !calendarDayInstanceId && !activeInstances?.length) {
              return null
            }
            if (
              !isPersistent &&
              getInstancesCompletedForCalendarDay(instances, moment(day)).length
            ) {
              return null
            }

            const instanceId =
              calendarDayInstanceId ||
              (!isPersistent ? activeInstances?.[0]?.instance_id : null) ||
              instrument?.instance_id

            if (!instanceId) return null

            const instanceForSelectedDay = instances?.find(
              (instance) => instance.instance_id === instanceId,
            )

            if (instanceForSelectedDay?.completion) return null
            if (!instanceForSelectedDay && !isPersistent && !activeInstances?.length) {
              return null
            }

            const submissionSurvey = formatSubmissionSurvey({
              ...instrument,
              instance_id: instanceId,
            })
            const storedInstrument = getInProgressInstrumentForActivities({
              instrument,
              participantId,
              instanceId,
            })

            const isStoredInstanceCompleted =
              storedInstrument?.instance_id &&
              completedInstanceKeys.has(
                `${instrumentId}:${storedInstrument.instance_id}`,
              )
            const canBeResumed =
              !isStoredInstanceCompleted &&
              canResumeInProgressInstrument(instrument, storedInstrument)

            const expirationText = getExpirationText(
              schedule,
              instanceForSelectedDay
                ? [instanceForSelectedDay]
                : activeInstances,
              resolveInstrumentVisibilityConfig(
                instrument,
                instanceForSelectedDay || activeInstances?.[0],
              ) || schedule?.visibility,
            );
            const displayKey =
              instrument.displayKey ||
              instrument.display_key ||
              `${instrumentId}:${scheduleId || 'default'}:${instanceId || 'none'}`

            return (
              <Link
                to={surveyUrl}
                key={`link-${displayKey}`}
                onClick={e => {
                  e.stopPropagation()
                  logSentryEvent({
                    message: SENTRY_MESSAGES.instrumentOpened,
                    tags: {
                      flow: 'instrument',
                      action: 'opened',
                      instrument_id: instrumentId != null ? String(instrumentId) : undefined,
                      instance_id: instanceId != null ? String(instanceId) : undefined,
                      instrument_type: type,
                      'activities.mode': 'calendar',
                      'submission.action': canBeResumed ? 'resume' : 'start',
                    },
                    breadcrumb: {
                      category: 'instrument',
                      message: `Instrument Opened ${canBeResumed ? 'resume' : 'start'}`,
                    },
                  })
                  setSubmissionSurvey(
                    canBeResumed && storedInstrument
                      ? storedInstrument
                      : submissionSurvey
                  )
                }}>
                {canBeResumed ? (
                  <div className='status-badge'>
                    <div className='status-badge-text'>{getStatusText()}</div>
                  </div>
                ) : null}
                <div className='instrument-icon'>{instrumentIcon()}</div>
                <div className='instrument-content'>
                  <div className='instrument-status-box'>
                    <div className={`type-tag ${type.toLowerCase()}`}>
                      {strings[type.toLowerCase()]}
                    </div>
                    {expirationText && (
                      <div className='expiration-tag'>
                        <div className='expiration-icon'>{clockIcon()}</div>
                        <div className='expiration-text'>
                          {expirationText}
                        </div>
                      </div>
                    )}
                  </div>

                  <InstrumentTitle
                    text={parseLocalizedTitle({ selectedLocale, instrument })}
                  />

                  {canBeResumed ? (
                    <Button
                      type='button'
                      className='resume-button calendar'
                      content={'Resume'}
                      onClick={e => {
                        e.preventDefault()
                        e.stopPropagation()
                        logSentryEvent({
                          message: SENTRY_MESSAGES.instrumentOpened,
                          tags: {
                            flow: 'instrument',
                            action: 'opened',
                            instrument_id: instrumentId != null ? String(instrumentId) : undefined,
                            instance_id: instanceId != null ? String(instanceId) : undefined,
                            instrument_type: type,
                            'activities.mode': 'calendar',
                            'submission.action': 'resume',
                          },
                          breadcrumb: {
                            category: 'instrument',
                            message: 'Instrument Opened resume',
                          },
                        })
                        setSubmissionSurvey(
                          canBeResumed && storedInstrument
                            ? storedInstrument
                            : submissionSurvey
                        )
                        if (surveyUrl) navigateTo(surveyUrl)
                      }}
                    />
                  ) : null}
                </div>
              </Link>
            )
          })}

          {completedInstrumentsForDay?.length ? (
            <CompletedInstrumentsList
              completedInstruments={completedInstrumentsForDay}
              studyId={studyId}
              participantId={participantId}
              setSubmissionSurvey={setSubmissionSurvey}
              formatSubmissionSurvey={formatSubmissionSurvey}
              selectedLocale={selectedLocale}
            />
          ) : null}

          {expiredInstrumentsForDay?.length ? (
            <ExpiredInstrumentsList
              expiredInstruments={expiredInstrumentsForDay}
            />
          ) : null}
        </div>
      )}
    </div>
  )
}

export default CalendarExpandedDay
